import React, { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Loader2 } from "lucide-react";
import { API } from "../lib/api";

/**
 * Play a chat message aloud using Le Gang's warm collective voice.
 * Caches audio per-message so re-plays don't re-hit the API.
 */
export default function VoicePlayButton({ text, lang, testId }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const toggle = async () => {
    if (state === "playing") {
      audioRef.current?.pause();
      audioRef.current.currentTime = 0;
      setState("idle");
      return;
    }
    if (urlRef.current) {
      const a = new Audio(urlRef.current);
      audioRef.current = a;
      a.onended = () => setState("idle");
      setState("playing");
      a.play();
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 3800), lang }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setState("idle");
      setState("playing");
      a.play();
    } catch (e) {
      setState("idle");
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Volume2;
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId || "voice-play"}
      aria-label="Play voice"
      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/60 hover:bg-white text-[#3D405B] transition-colors"
    >
      <Icon className={`w-4 h-4 ${state === "loading" ? "animate-spin" : ""}`} />
    </button>
  );
}
